import { FilterIcon } from '@/components/icons/filter-icon';
import GeneralLayout from '@/components/layouts/_general';
import { Grid } from '@/components/products/grid';
import SearchCount from '@/components/search-view/search-count';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import StickyBox from 'react-sticky-box';
import dynamic from 'next/dynamic';
import Seo from '@/components/seo/seo';
import { MAIN_SEARCH_INDEX_ID } from '@/lib/services/typesense/constants';
import { Product } from '@/types';
import useLayout from '@/lib/hooks/use-layout';
import {
  useSearchBox,
  useInstantSearchContext,
  useInfiniteHits,
} from 'react-instantsearch';
import { useEffect, useMemo, useState } from 'react';
import SearchPageProvider from '@/lib/services/typesense/search-providers/search';
import ManagedDrawer from '@/components/ui/drawer/managed-drawer';
import { useDrawer } from '@/components/ui/drawer/useDrawer';
import { searchDrawerAtom } from '../../store/searchDrawerAtom';
import CartCounterButton from '@/components/cart/cart-counter-button';
import { useWindowSize } from 'react-use';
import SidebarFilter from '@/components/search-view/sidebar-filter-and-categories';
import SearchDidYouMean from '@/components/search-view/search-did-you-mean';

const MobileNavigation = dynamic(
  () => import('@/components/layouts/mobile-navigation'),
  {
    ssr: false,
  },
);

export { getServerSideProps } from '@/framework/search.ssr';

function queryParam(
  query: Record<string, unknown>,
  key: string,
): string {
  const raw = query[key];
  if (typeof raw === 'string') return raw.trim();
  if (Array.isArray(raw) && typeof raw[0] === 'string') return raw[0].trim();

  const [root, ...rest] = key.split(/\[|\]/).filter(Boolean);
  let current: unknown = query[root];
  for (const part of rest) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) {
      return '';
    }
    current = (current as Record<string, unknown>)[part];
  }
  if (typeof current === 'string') return current.trim();
  if (Array.isArray(current) && typeof current[0] === 'string') {
    return current[0].trim();
  }
  return '';
}

function queryKeys(query: Record<string, unknown>): string[] {
  return Object.keys(query).flatMap((key) => {
    const value = query[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value as Record<string, unknown>).map(
        (inner) => `${key}[${inner}]`,
      );
    }
    return [key];
  });
}

/**
 * `/search/?…categories.name…` with no text query is a duplicate of
 * `/categorias/{slug}/`. Other faceted/text result URLs stay indexable.
 */
function isCategoryFacetDuplicate(
  query: Record<string, unknown>,
): boolean {
  if (queryParam(query, `${MAIN_SEARCH_INDEX_ID}[query]`)) return false;

  const keys = queryKeys(query);
  const categoryKeys = keys.filter(
    (key) =>
      key.includes('[refinementList]') && key.includes('categories.name'),
  );
  if (categoryKeys.length !== 1) return false;

  return !keys.some(
    (key) =>
      key.includes('[refinementList]') && !key.includes('categories.name'),
  );
}

function SearchDocumentHead() {
  const router = useRouter();
  const searchTerm = queryParam(
    router.query as Record<string, unknown>,
    `${MAIN_SEARCH_INDEX_ID}[query]`,
  );
  const title = searchTerm
    ? `Busca: ${searchTerm}`
    : 'Busca de produtos | Qualidoc Farmácia Online 24 horas';
  const description = searchTerm
    ? `Resultados da busca por ${searchTerm} na Qualidoc, farmácia online com entrega em todo o Brasil.`
    : 'Encontre medicamentos, genéricos, dermocosméticos e produtos de saúde na Qualidoc.';

  return (
    <>
      <Seo
        title={title}
        description={description}
        url="search"
        noindex={isCategoryFacetDuplicate(
          router.query as Record<string, unknown>,
        )}
        nofollow={false}
      />
      <h1 className="sr-only">
        {searchTerm ? `Busca: ${searchTerm}` : 'Busca de produtos'}
      </h1>
    </>
  );
}

export default function SearchPage() {
  const { layout } = useLayout();
  const { width } = useWindowSize();
  const { query } = useSearchBox();
  const { results, items } = useInfiniteHits();

  const paginatorInfo = useMemo(() => {
    return {
      total: results?.nbHits,
      items_count: items.length ?? 0,
    };
  }, [results, items]);

  // TODO: review how capture errors from InstantSearch
  // if (error) return <ErrorMessage message={error.message} />;

  return (
    <div className="w-full">
      <div className="flex flex-col items-center justify-between mb-7 md:flex-row">
        {/* //FIXME: */}
        <SearchCount
          items_count={paginatorInfo?.items_count}
          total={
            //@ts-ignore
            paginatorInfo?.total ?? 0
          }
        />
        {query && (
          <div className="max-w-xs mt-4 md:mt-0">
            <h3>Você buscou por: {query ?? ''}</h3>
          </div>
        )}
      </div>
      <ProductsWrapper column="auto" />
      {!['compact', 'minimal'].includes(layout) && width > 1023 && (
        <CartCounterButton />
      )}
    </div>
  );
}
const ProductsWrapper = ({ column }: { column: any }) => {
  const { isLastPage, showMore, items, results } = useInfiniteHits({
    showPrevious: true,
  });

  const { status } = useInstantSearchContext();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(results?.query === '' || status !== 'idle');
  }, [status, results]);

  // TODO: review refresh page to remove filter or back to initial page

  return (
    <Grid
      products={items as unknown as Product[] | undefined}
      loadMore={showMore}
      isLoadingMore={isLoading}
      hasMore={!isLastPage}
      error={false}
      isLoading={isLoading}
      column={column}
      emptyAccessory={<SearchDidYouMean />}
    />
  );
};

const SearchPageLayout = ({ page }: { page: React.ReactElement }) => {
  const { openDrawer } = useDrawer({
    atom: searchDrawerAtom,
  });

  return (
    <>
      <div className="w-full max-w-[1560px] mx-auto">
        <div className="flex w-full min-h-screen px-5 pt-8 pb-24 mx-auto max-w-1920 rtl:space-x-reverse lg:space-x-10 xl:py-14 xl:px-16">
          <div className="hidden w-80 shrink-0 lg:block">
            <StickyBox offsetTop={140} offsetBottom={30}>
              <SidebarFilter variables={page?.props?.variables?.categories} />
            </StickyBox>
          </div>
          {page}
        </div>
      </div>
      <MobileNavigation className="z-50">
        <motion.button
          whileTap={{ scale: 0.88 }}
          onClick={() =>
            openDrawer('SEARCH_FILTER', {
              variables: page?.props?.variables?.categories,
            })
          }
          className="h-full focus:text-accent focus:outline-0"
        >
          <span className="sr-only">Filtrar</span>
          <FilterIcon className="text-color-primary mx-auto h-5" />
          <span className="text-xs font-semibold text-color-primary">
            Filtros
          </span>
        </motion.button>
      </MobileNavigation>
      <ManagedDrawer atom={searchDrawerAtom} />
    </>
  );
};

const GetLayout = (page: React.ReactElement) => (
  <>
    <SearchDocumentHead />
    <SearchPageProvider>
      <GeneralLayout>
        <SearchPageLayout page={page} />
      </GeneralLayout>
    </SearchPageProvider>
  </>
);

SearchPage.getLayout = GetLayout;
